import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Icon from '../components/Icon';

// Help content per role
const HELP_CONTENT = {
  user: {
    title: 'User Guide',
    titleIcon: 'bike',
    subtitle: 'Welcome to Frame-Book! Everything you need to know.',
    sections: [
      {
        id: 'getting-started',
        iconName: 'rocket',
        title: 'Getting started',
        content: [
          {
            q: 'What is Frame-Book?',
            a: 'Frame-Book is your bike\'s digital passport. Every service, repair, and maintenance kept in one place, forever.'
          },
          {
            q: 'How do I start?',
            a: 'Register for free, add your bike, and find nearby service shops. The shop records the work automatically in your Bike Passport.'
          }
        ]
      },
      {
        id: 'bikes',
        iconName: 'bike',
        title: 'My bikes',
        content: [
          {
            q: 'How do I add a bike?',
            a: 'From the "My Bike Passports" menu. Provide brand, model, year, and frame number (if available). Each bike gets its own digital passport.',
            action: { label: 'Add bike', iconName: 'upload', path: '/profile' }
          },
          {
            q: 'What is the Bike Passport?',
            a: 'Your bike passport is a complete digital record of EVERYTHING about your bike: photos, service history, repairs, maintenance, notes, registrations, and all bike details in one place. Think of it as your bike\'s official documentation.',
            action: { label: 'My Bike Passports', iconName: 'bikePassports', path: '/bikes' }
          },
          {
            q: 'Can I upload photos?',
            a: 'Yes. Your Bike Passport includes a photo gallery. Capture your bike from multiple angles to document its condition.',
            action: { label: 'My Bike Passports', iconName: 'bikePassports', path: '/bikes' }
          }
        ]
      },
      {
        id: 'booking',
        iconName: 'calendar',
        title: 'Booking',
        content: [
          {
            q: 'How do I book an appointment?',
            a: 'Go to "Book Service", pick the shop, your bike, and the service type. Choose an available slot and you\'re done.',
            action: { label: 'Book service', iconName: 'calendar', path: '/book' }
          },
          {
            q: 'Can I modify a booking?',
            a: 'See bookings on the Dashboard. You can cancel before the shop accepts; after acceptance, contact the shop directly.',
            action: { label: 'Dashboard', iconName: 'home', path: '/dashboard' }
          }
        ]
      },
      {
        id: 'companies',
        iconName: 'shops',
        title: 'Service shops',
        content: [
          {
            q: 'How do I find a shop?',
            a: 'Use "Find Service Shops" to browse registered shops. Filter by location, rating, and services.',
            action: { label: 'Find shops', iconName: 'view', path: '/companies' }
          },
          {
            q: 'What is "Connect"?',
            a: 'When you connect to a shop, they can view your bikes and serve you faster. Optional but recommended.',
            action: { label: 'Shops', iconName: 'shops', path: '/companies' }
          }
        ]
      },
      {
        id: 'reviews',
        iconName: 'star',
        title: 'Reviews',
        content: [
          {
            q: 'How do I leave a review?',
            a: 'After each completed service you’ll get a prompt to rate 1-5 stars and add comments.'
          },
          {
            q: 'Why are reviews important?',
            a: 'They help riders choose trustworthy shops and give shops feedback on their work.'
          }
        ]
      }
    ]
  },
  technician: {
    title: 'Technician Guide',
    titleIcon: 'technician',
    subtitle: 'Everything you need to know as a technician.',
    sections: [
      {
        id: 'daily-work',
        iconName: 'service',
        title: 'Daily work',
        content: [
          {
            q: 'How do I see today’s jobs?',
            a: 'Check the Dashboard for today’s bookings. Use the calendar for weekly/monthly view.'
          },
          {
            q: 'How do I record service work?',
            a: 'Open the booking, add work done, parts, and costs. After saving, it\'s stored in the customer\'s Bike Passport.'
          }
        ]
      },
      {
        id: 'bikes-services',
        iconName: 'bike',
        title: 'Bikes & services',
        content: [
          {
            q: 'How do I see a bike\'s history?',
            a: 'On the bike page, open "Bike Passport" to view the full service history from any shop.'
          },
          {
            q: 'What does a "locked" service mean?',
            a: 'Locked entries can\'t be edited, but you can still add comments. This preserves history integrity.'
          }
        ]
      },
      {
        id: 'comments',
        iconName: 'chat',
        title: 'Communication',
        content: [
          {
            q: 'How do I add a comment?',
            a: 'In a service entry, click "Comments" to add notes. You can tag them (question, issue, resolution, etc.).'
          },
          {
            q: 'Can the customer see comments?',
            a: 'Yes, service comments are visible to the customer. Great for sharing key info.'
          }
        ]
      },
      {
        id: 'support',
        iconName: 'view',
        title: 'Support',
        content: [
          {
            q: 'Where do I get help?',
            a: 'Open a ticket in "Support". Choose a category, describe the issue, and we’ll respond soon.'
          }
        ]
      }
    ]
  },
  company_admin: {
    title: 'Service Admin Guide',
    titleIcon: 'companies',
    subtitle: 'Manage your shop in Frame-Book.',
    sections: [
      {
        id: 'dashboard',
        iconName: 'analytics',
        title: 'Dashboard',
        content: [
          {
            q: 'What do I see on the Dashboard?',
            a: 'You get an overview of today\'s bookings, statistics, recent services, and reviews. Quick access to everything.'
          },
          {
            q: 'How do I interpret the statistics?',
            a: 'The numbers show your service performance: bikes, services, customers, ratings. Based on these, you can also earn achievements!'
          }
        ]
      },
      {
        id: 'bookings',
        iconName: 'calendar',
        title: 'Manage bookings',
        content: [
          {
            q: 'How do I accept bookings?',
            a: 'In calendar or Dashboard, open a booking and choose "Accept" or "Reject".'
          },
          {
            q: 'Can I set opening hours?',
            a: 'Yes. In Settings you can set hours, holidays, and slot intervals.'
          }
        ]
      },
      {
        id: 'services-bikes',
        iconName: 'service',
        title: 'Services & bikes',
        content: [
          {
            q: 'How do I create a service?',
            a: 'In "Bikes & Services", add services with name, description, duration, and price.'
          },
          {
            q: 'How do I record service work?',
            a: 'Select the bike, add a new service entry with description and pricing. On save, it’s in the Bike Passport.'
          },
          {
            q: 'Can I register a new bike?',
            a: 'Yes. If the rider hasn’t added it, you can register it under "Bikes & Services".'
          }
        ]
      },
      {
        id: 'team',
        iconName: 'users',
        title: 'Team management',
        content: [
          {
            q: 'How do I add a technician?',
            a: 'In Settings → Team, invite new technicians by email; they\'ll get an invite.'
          },
          {
            q: 'What permissions do they have?',
            a: 'Technicians see bookings and can record services, but cannot edit company settings.'
          }
        ]
      },
      {
        id: 'achievements',
        iconName: 'achievement',
        title: 'Achievements',
        content: [
          {
            q: 'What are achievements?',
            a: 'Milestones for your shop performance. Bikes, services, customers, reviews can unlock rewards.'
          },
          {
            q: 'Are there rewards?',
            a: 'Collect all achievements to get 10% off your Frame-Book subscription.'
          }
        ]
      },
      {
        id: 'settings',
        iconName: 'settings',
        title: 'Settings',
        content: [
          {
            q: 'Where do I edit company info?',
            a: 'In Settings: name, address, contacts, logo, opening hours.'
          },
          {
            q: 'Can I upload a logo?',
            a: 'Yes. Settings → Company logo. It shows on your profile and to customers.'
          }
        ]
      },
      {
        id: 'integrations-square',
        iconName: 'integration',
        title: 'Integrations',
        content: [
          {
            q: 'How do service tickets work with Square?',
            a: 'You can create a service ticket in Frame-Book by picking items from your synced Square catalog and linking a bike. The ticket becomes an in-progress service. When the job is done, open the service and process payment via Square Terminal, Cash, or mark it as already paid on your Square POS.'
          },
          {
            q: 'What happens when I take a payment on my Square POS directly?',
            a: 'Frame-Book automatically imports completed Square orders via webhooks. They appear under "Square Tickets" in the Services panel. If the order note or ticket name contains a frame number, the service is auto-linked to the matching bike.'
          },
          {
            q: 'How do Square bookings work?',
            a: 'Appointments from Square Appointments are synced into Frame-Book and appear in your services list as upcoming (scheduled) jobs. You can trigger a manual sync from Settings → Integrations, or they sync automatically.'
          },
          {
            q: 'What payment methods are supported?',
            a: 'Three options: (1) Square Terminal — sends a checkout request to your terminal device. (2) Cash — records a cash payment. (3) Already paid in Square POS — marks the service as completed when you\'ve already charged via your register or POS app.'
          },
          {
            q: 'Can I upload receipts for services?',
            a: 'Yes. Open any existing service from the services list, scroll to the Receipts section, and upload receipt images. Receipts are stored separately and can be added at any time after the service is created.'
          }
        ]
      }
    ]
  },
  super_admin: {
    title: 'Super Admin Guide',
    titleIcon: 'admin',
    subtitle: 'Administer the entire Frame-Book system.',
    sections: [
      {
        id: 'overview',
        iconName: 'integration',
        title: 'Overview',
        content: [
          {
            q: 'What can I see as Super Admin?',
            a: 'Full access to all companies, users, bikes, and services. Dashboard shows aggregated stats.'
          }
        ]
      },
      {
        id: 'companies',
        iconName: 'companies',
        title: 'Manage companies',
        content: [
          {
            q: 'How do I create a company?',
            a: 'In "Admin Panel" → "Companies", create a company and assign an admin user.'
          },
          {
            q: 'How do I edit company details?',
            a: 'Click a company in the list and edit in the form shown.'
          }
        ]
      },
      {
        id: 'users',
        iconName: 'users',
        title: 'Users',
        content: [
          {
            q: 'How do I manage users?',
            a: 'In "Admin Panel" → "Users" you can edit, delete, or change roles.'
          }
        ]
      },
      {
        id: 'tickets',
        iconName: 'view',
        title: 'Support tickets',
        content: [
          {
            q: 'Where are tickets?',
            a: '"Admin Panel" → "Support Tickets" shows all tickets. Reply and close them there.'
          }
        ]
      },
      {
        id: 'analytics',
        iconName: 'analytics',
        title: 'Analytics',
        content: [
          {
            q: 'What analytics are available?',
            a: '"Analytics" shows detailed reports: registrations, services, revenue, trends.'
          }
        ]
      }
    ]
  }
};

// General FAQ for all roles
const GENERAL_FAQ = [
  {
    q: 'Forgot password',
    a: 'On the login page click "Forgot password" and follow the steps.'
  },
  {
    q: 'How do I edit my profile?',
    a: 'In "Profile" you can update your name, email, and password.'
  },
  {
    q: 'Switch dark/light mode',
    a: 'Use the sun/moon toggle at the bottom of the sidebar.'
  },
  {
    q: 'Who do I contact for issues?',
    a: 'Open a ticket in "Support" or email us: support@frame-book.com'
  }
];

function HelpPage({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [openSection, setOpenSection] = useState(null);
  const [openQuestion, setOpenQuestion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Content per role
  const role = user?.role || 'user';
  const roleKey = role === 'admin' ? 'company_admin' : role;
  const content = HELP_CONTENT[roleKey] || HELP_CONTENT.user;

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const sectionId = (params.get('section') || '').trim();
    if (!sectionId) return;
    const exists = (content.sections || []).some((section) => section.id === sectionId);
    if (!exists) return;
    setOpenSection(sectionId);
    setOpenQuestion(null);
  }, [location.search, content.sections]);

  // Search within content
  const filterContent = (sections) => {
    if (!searchTerm.trim()) return sections;
    
    const term = searchTerm.toLowerCase();
    return sections.map(section => ({
      ...section,
      content: section.content.filter(item => 
        item.q.toLowerCase().includes(term) || 
        item.a.toLowerCase().includes(term)
      )
    })).filter(section => section.content.length > 0);
  };

  const filteredSections = filterContent(content.sections);
  const filteredGeneral = GENERAL_FAQ.filter(item =>
    !searchTerm.trim() ||
    item.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.a.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout
      user={user}
      onLogout={onLogout}
      pageTitle="Help & Guide"
      pageSubtitle="Find everything you need to know about Frame-Book"
      darkMode={darkMode}
      onToggleDarkMode={onToggleDarkMode}
    >
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(61, 90, 32, 0.15) 0%, rgba(16, 185, 129, 0.1) 100%)',
        border: '1px solid rgba(61, 90, 32, 0.3)',
        borderRadius: '24px',
        padding: '32px',
        marginBottom: '32px',
        textAlign: 'center'
      }}>
        <h1 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '28px', 
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px'
        }}>
          {content.titleIcon && <Icon name={content.titleIcon} color="primary" darkMode={darkMode} size={28} />}
          {content.title}
        </h1>
        <p style={{ 
          margin: 0, 
          color: 'rgba(255,255,255,0.7)',
          fontSize: '16px'
        }}>
          {content.subtitle}
        </p>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '28px' }}>
        <div style={{
          position: 'relative',
          maxWidth: '500px',
          margin: '0 auto'
        }}>
          <span style={{
            position: 'absolute',
            left: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            opacity: 0.5
          }}>
            <Icon name="view" color="secondary" darkMode={darkMode} size={18} />
          </span>
          <input
            type="text"
            placeholder="Search the help..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '14px 16px 14px 48px',
              fontSize: '15px',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.03)',
              color: '#ffffff',
              outline: 'none'
            }}
          />
        </div>
      </div>

      {/* Quick Links */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '12px',
        marginBottom: '32px'
      }}>
        {content.sections.map(section => (
          <button
            key={section.id}
            onClick={() => {
              setOpenSection(openSection === section.id ? null : section.id);
              setOpenQuestion(null);
            }}
            style={{
              padding: '16px',
              background: openSection === section.id 
                ? 'rgba(61, 90, 32, 0.2)' 
                : 'rgba(255,255,255,0.03)',
              border: openSection === section.id 
                ? '1px solid rgba(61, 90, 32, 0.4)' 
                : '1px solid rgba(255,255,255,0.06)',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              textAlign: 'center'
            }}
          >
            <Icon name={section.iconName} color={openSection === section.id ? "brand" : "primary"} darkMode={darkMode} size={24} style={{ marginBottom: '6px' }} />
            <div style={{ 
              fontSize: '13px', 
              color: openSection === section.id ? '#3d5a20' : 'rgba(255,255,255,0.8)',
              fontWeight: '500'
            }}>
              {section.title}
            </div>
          </button>
        ))}
      </div>

      {/* Sections content */}
      {filteredSections.length === 0 && searchTerm && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'rgba(255,255,255,0.5)'
        }}>
          <Icon name="view" color="muted" darkMode={darkMode} size={48} style={{ marginBottom: '16px' }} />
          <p>No results for: "{searchTerm}"</p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {filteredSections.map(section => (
          <div
            key={section.id}
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '16px',
              overflow: 'hidden'
            }}
          >
            {/* Section Header */}
            <button
              onClick={() => setOpenSection(openSection === section.id ? null : section.id)}
              style={{
                width: '100%',
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              <Icon name={section.iconName} color="primary" darkMode={darkMode} size={28} />
              <span style={{ 
                flex: 1, 
                fontSize: '17px', 
                fontWeight: '600',
                color: '#ffffff'
              }}>
                {section.title}
              </span>
              <span style={{ 
                fontSize: '18px', 
                color: 'rgba(255,255,255,0.4)',
                transform: openSection === section.id ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.2s'
              }}>
                ▼
              </span>
            </button>

            {/* Section Content */}
            {openSection === section.id && (
              <div style={{ 
                padding: '0 24px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {section.content.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: 'rgba(255,255,255,0.02)',
                      borderRadius: '10px',
                      overflow: 'hidden'
                    }}
                  >
                    <button
                      onClick={() => setOpenQuestion(
                        openQuestion === `${section.id}-${idx}` 
                          ? null 
                          : `${section.id}-${idx}`
                      )}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: '500',
                        color: 'rgba(255,255,255,0.9)'
                      }}>
                        {item.q}
                      </span>
                      <span style={{ 
                        fontSize: '14px', 
                        color: 'rgba(255,255,255,0.4)',
                        marginLeft: '12px'
                      }}>
                        {openQuestion === `${section.id}-${idx}` ? '−' : '+'}
                      </span>
                    </button>
                    
                    {openQuestion === `${section.id}-${idx}` && (
                      <div style={{
                        padding: '0 16px 16px',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563'
                      }}>
                        {item.a}
                        {item.action && (
                          <button
                            onClick={() => navigate(item.action.path)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              marginTop: '12px',
                              padding: '8px 16px',
                              background: 'rgba(61, 90, 32, 0.15)',
                              border: '1px solid rgba(61, 90, 32, 0.3)',
                              borderRadius: '8px',
                              color: '#3d5a20',
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(61, 90, 32, 0.25)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(61, 90, 32, 0.15)';
                            }}
                          >
                            {item.action.iconName && <Icon name={item.action.iconName} color="brand" darkMode={darkMode} size={14} />}
                            {item.action.label}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* General FAQ */}
      {filteredGeneral.length > 0 && (
        <div style={{ marginTop: '40px' }}>
          <h2 style={{ 
            fontSize: '20px', 
            color: '#ffffff',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Icon name="info" color="primary" darkMode={darkMode} size={20} />
            Frequently Asked Questions
          </h2>
          
          <div style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '16px',
            padding: '20px'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredGeneral.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '16px',
                    background: 'rgba(255,255,255,0.02)',
                    borderRadius: '10px'
                  }}
                >
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '600',
                    color: '#ffffff',
                    marginBottom: '8px'
                  }}>
                    {item.q}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: 'rgba(255,255,255,0.7)'
                  }}>
                    {item.a}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Contact */}
      <div style={{
        marginTop: '40px',
        padding: '28px',
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.08) 100%)',
        border: '1px solid rgba(59, 130, 246, 0.2)',
        borderRadius: '20px',
        textAlign: 'center'
      }}>
        <Icon name="chat" color="info" darkMode={darkMode} size={36} style={{ marginBottom: '12px' }} />
        <h3 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '18px',
          color: '#ffffff'
        }}>
          Didn’t find your answer?
        </h3>
        <p style={{ 
          margin: '0 0 20px 0', 
          color: 'rgba(255,255,255,0.6)',
          fontSize: '14px'
        }}>
          Open a support ticket or email us directly.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a
            href="/support"
            className="btn btn-primary"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
          >
            <Icon name="view" color="primary" darkMode={darkMode} size={16} />
            Support Ticket
          </a>
          <a
            href="mailto:support@frame-book.com"
            className="btn btn-secondary"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
          >
            <Icon name="email" color="secondary" darkMode={darkMode} size={16} />
            Send email
          </a>
        </div>
      </div>

    </Layout>
  );
}

export default HelpPage;
